/* eslint-disable no-unused-expressions */
import EasyLogin from '@hb/easy-login';
import axios from 'axios';
import qs from 'qs';
import {
  BusinessError, SystemError, NetworkError,
} from '@/types/error';
import { Toast } from 'vant';
import { loadHbridge } from '@/utils/globalLoader';
import { getCurrentRoutePath } from '@/utils/route';
import store from '@/store';

const pkg = require('../../package.json');

const iosActionCatch = {};

// hbridge内部使用此变量做多环境缓存隔离
window.env = process.env;

/**
 * 登录校验接口白名单
 * 解决本地存在过期token时，调用无登录校验接口仍会校验登录问题
 */
const LOGIN_VALID_WHITE_API_LIST = [
  'switchpower.evhome.coupon.active.config',
];

/**
 * 登录校验路由白名单（仅未登录时生效）
 * 在路由地址中追加 ignoreWhiteListValid=1 参数，可以忽略白名单
 */
const LOGIN_VALID_WHITE_ROUTE_LIST = [
  '/one-yuan-welfare-card/buy',
];

/** 获取当前路由是否需要校验登录 */
const getCurrentRouteIsLoginValid = () => {
  const currentRouteHash = getCurrentRoutePath();
  const currentRoutePath = currentRouteHash.split('?')[0];
  return !LOGIN_VALID_WHITE_ROUTE_LIST.includes(currentRoutePath);
};

/** 校验接口是否需要登录校验 */
const getApiIsLoginValid = (api) => !LOGIN_VALID_WHITE_API_LIST.includes(api);

const esayLogin = async () => {
  const { loginTitle } = qs.parse((window.location.href).split('?').pop());
  EasyLogin.signIn({
    codeEnv: process.env.VUE_APP_ENV, // vue项目请使用 VUE_APP_ENV
    loginTitle: (loginTitle && window.location.href.includes('recommend-officer'))
      ? decodeURIComponent(loginTitle)
      : '小哈换电', // 默认 欢迎使用哈啰出行
  });
};


const getBaseData = async () => {
  const { env, Native } = await loadHbridge();
  let systemCode = 'A5';
  let { version } = pkg;
  if (env.isEasyBikeApp) {
    // 哈啰出行 APP
    version = env.appVersion;
    const { os } = env.platform;
    if (os === 'Android') {
      systemCode = '62';
    } else if (os === 'IOS') {
      systemCode = '61';
    }
  } else if (env.isAliMP || env.isAliMPMonitor) {
    try {
      version = await Native.callNative({
        method: 'getVersion',
        classMap: 'SwitchBridge',
      });
    } catch (e) {
      // empty
    }
  }
  return {
    version,
    systemCode,
  };
};

const defaultRequestDataPromise = getBaseData();

const service = axios.create({
  timeout: 10000,
  headers: {
    'content-type': 'application/json',
  },
});

let tokenCache;
async function getTokenAndKey() {
  if (store.state.authInfo.token) {
    return {
      token: store.state.authInfo.token,
      ticket: store.state.authInfo.ticket,
    };
  }
  if (!tokenCache) {
    const { Native } = await loadHbridge();

    tokenCache = Native.getTokenAndKey();
  }
  return tokenCache;
}

service.interceptors.request.use(
  async (config) => {
    if (window.__PRERENDER_INJECTED) {
      config.data = config.data || {};
      Object.assign(config.data, { version: pkg.version, systemCode: 'A5' });
      if (config.data.action) {
        config.url = `${config.url}?action=${config.data.action}`;
      }
      return config;
    }
    const { env } = await loadHbridge();
    config.data = config.data || {};
    let _headersAppend = {};
    if (window.helloOfflineMode === 1 && env.isIOS && env.isEasyBikeApp && config.cacheKey) {
      _headersAppend = { __native_cache_key__: config.cacheKey };
    }
    config.headers = config.headers ? {
      ..._headersAppend,
      ...config.headers,
    } : _headersAppend;
    Object.assign(config.data, await defaultRequestDataPromise);
    if (config.data.action) {
      config.url = `${config.url}?${config.data.action}`;
    }
    if (!config.ignoreLogin) {
      const isValidLogin = getCurrentRouteIsLoginValid();
      try {
        const { token, ticket } = await getTokenAndKey();
        // 接口登录校验白名单校验
        if (getApiIsLoginValid(config.data.action)) {
          config.data.token = token;
          config.data.ticket = ticket;
        }
      } catch (error) {
        // 页面登录校验白名单校验
        if (isValidLogin) {
          tokenCache = null;
          if (!env.isEasyBikeApp && !env.isAliMP && !env.isAliMPMonitor) {
            esayLogin();
          }
          return Promise.reject(new SystemError('请重新登录'));
        }
      }

      // config.data.__sysTag = 'tt-user-99';
      // config.data.__sysTag = 'zl';
      // config.data.__sysTag = 'tt-card-1376';

      if (config.data.action && env.isEasyBikeApp && env.isIOS) {
        if (!iosActionCatch[config.data.action]) {
          iosActionCatch[config.data.action] = true;
        } else {
          config.data.$iosRepeat = 1;
        }
      }
    }

    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

service.interceptors.response.use(
  (response) => {
    const { config = {} } = response || {};
    const { ignoreCodes = [], toastOnError = true } = config;
    // 各种返回状况处理
    const res = response && response.data;
    if (res && res.code === 103) {
      const message = res.msg;
      loadHbridge().then(({ env, Native }) => {
        const isValidLogin = getCurrentRouteIsLoginValid();
        // 登录校验白名单校验
        if (isValidLogin) {
          if (env.isEasyBikeApp || env.isAliMP || env.isAliMPMonitor) {
            toastOnError && Toast(message);
            Native.logout();
          } else {
            esayLogin();
          }
        }
      });
      throw new BusinessError({
        requestOptions: config.data,
        response,
        message,
      });
    }
    if (!res || (res.code !== 0 && ignoreCodes.indexOf(res.code) === -1)) {
      const message = res.msg || '数据异常';
      toastOnError && Toast(message);
      throw new BusinessError({
        requestOptions: config.data,
        response,
        message,
      });
    }
    return res;
  },
  (error) => {
    if (process.env.VUE_APP_ENV !== 'pro') {
      // eslint-disable-next-line no-console
      console.dir(error);
    }
    const { config = {} } = error || {};
    const { toastOnError = true } = config;
    if (error && error.message === 'Network Error') {
      const networkError = new NetworkError({
        requestOptions: error.config.data,
        response: error.response,
        message: '网络异常,请稍后重试',
      });
      toastOnError && Toast(networkError.message);
      return Promise.reject(networkError);
    }

    if (!(error instanceof SystemError)) {
      toastOnError && Toast('发生未知错误');
    }
    return Promise.reject(error);
  },
);

export default service;
